import { CircleHelp } from "lucide-react";
import React, { useState } from "react";

import {
  IgButton,
  IgCodeEditor,
  IgTooltip,
  IgTypography,
} from "@intelligems/ig-theme";
import { Box, Grid, IconButton } from "@mui/material";
import * as Sentry from "@sentry/react";
import type { ConfigurationInputProps } from "@src/types/organization";
import { toProperCaseWithSpaces } from "@src/utils";

export const JsonConfigInput = ({
  config,
  onChange,
  tooltipMessage,
  title,
}: ConfigurationInputProps) => {
  const [jsonData, setJsonData] = useState<string>(
    JSON.stringify(config.value, undefined, 2)
  );

  // validate json object
  const [validate, setValidate] = useState<boolean>(false);

  const parseOptionsInput = () => {
    if (jsonData.length < 1) {
      // if update json to nothing (empty)
      onChange({}); // set empty object
      setValidate(true);
    } else {
      try {
        const optionsData = JSON.parse(jsonData); // turn to Json object
        if (optionsData && typeof optionsData === "object") {
          onChange(optionsData); // set new value
          setValidate(true);
        }
      } catch (e) {
        Sentry.captureException(e);
        Sentry.captureMessage(
          "Advanced settings JSON input could not be parsed."
        );
      }
    }
  };

  return (
    <Grid
      container
      item
      columnGap={2}
      direction={"row"}
      sx={{
        alignItems: "center",
        flexWrap: "nowrap",
      }}
    >
      <Grid container item direction={"column"} rowSpacing={0.5}>
        <Grid item>
          <IgTypography>
            {title || toProperCaseWithSpaces(config.key)}
          </IgTypography>
          {tooltipMessage && (
            <IgTooltip title={tooltipMessage}>
              <IconButton size={"small"} aria-label="info">
                <CircleHelp size={18} />
              </IconButton>
            </IgTooltip>
          )}
        </Grid>
        <Grid item>
          <Box
            sx={{
              border: "solid 1px #CBCBCB",
              width: "100%",
            }}
          >
            <IgCodeEditor
              language="json"
              height="300px"
              value={jsonData}
              onChange={(e) => {
                setJsonData(e || "");
              }}
            />
          </Box>
        </Grid>
      </Grid>
      <Grid item>
        <IgButton variant="outlined" onClick={parseOptionsInput}>
          {validate ? "Validated!" : "Validate"}
        </IgButton>
      </Grid>
    </Grid>
  );
};
